import { useRoute } from 'vue-router'
import useGet from '../../../core/composables/api/useGet'
import { computed, watch, toValue, type MaybeRef } from 'vue'
import type { FeedbackTopic } from '../../../core/models/feedback-topic'
import type { Scale } from '../../../core/models/scale'
import type { Skill } from '../../../core/models/skill'
import type { QuestionAnalysis, SkillAnalysis, TextAnalysis } from '@/core/models/surveys-analytics'
import { DialogPhase } from '@/core/models/dialog/dialog'
import { dialogKey, dialogRoleKey } from './use-dialog-navigation'
import { injectStrict } from '@/core/utils/inject-strict'

export const useFinalEvaluationSlide = (feedbackTopicId: MaybeRef<string>) => {
  const route = useRoute()

  const url = computed(() => {
    return `dialog/${route.params.id}/evaluation-slide/${toValue(feedbackTopicId)}`
  })

  const dialog = injectStrict(dialogKey)
  const dialogRole = injectStrict(dialogRoleKey)

  const { data, load, status } = useGet<FinalDialogEvaluationSlideData>(url)

  const canSeeData = computed(() => {
    return (
      (dialogRole === 'coach' && dialog.phase === DialogPhase.CoachPreparation) ||
      (dialogRole === 'coach' && dialog.phase === DialogPhase.Conversation) ||
      (dialogRole === 'coach' && dialog.phase === DialogPhase.Conducted) ||
      dialogRole === 'coachee'
    )
  })

  const loadData = () => {
    if (canSeeData.value) {
      load()
    }
  }

  watch(
    url,
    () => {
      loadData()
    },
    { immediate: true },
  )

  return { data, status, canSeeData }
}

export interface FinalDialogEvaluationSlideData {
  feedbackTopic: FeedbackTopic & { skills: ReadonlyArray<Skill> }
  skillData: ReadonlyArray<SkillAnalysis>
  strengths: ReadonlyArray<QuestionAnalysis>
  potentials: ReadonlyArray<QuestionAnalysis>
  overestimates: ReadonlyArray<QuestionAnalysis>
  underestimates: ReadonlyArray<QuestionAnalysis>
  texts: ReadonlyArray<TextAnalysis>
  scale: Scale
}
